import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MapPin, Plus, Loader2, Building2, Users } from "lucide-react";

/**
 * Bases (geographic locations) admin. Create/rename/activate bases and
 * assign users to the bases they may access, with a default base used for
 * their bot entries. RBAC-gated by locations:manage.
 */
export default function LocationsPage() {
  const [bases, setBases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, u] = await Promise.all([api.get("/bases"), api.get("/bases/users")]);
      setBases(b.data?.bases || []);
      setUsers(u.data?.users || []);
    } catch (_e) {
      toast.error("Couldn't load bases");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createBase = async () => {
    if (!newName.trim()) { toast.error("Enter a base name"); return; }
    setCreating(true);
    try {
      await api.post("/bases", { name: newName.trim(), code: newCode.trim() });
      toast.success("Base created");
      setNewName(""); setNewCode("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create base");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.patch(`/bases/${b.id}`, { active: !b.active });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update base");
    }
  };

  const toggleUserBase = async (u, baseId) => {
    const current = new Set(u.base_ids || []);
    if (current.has(baseId)) current.delete(baseId); else current.add(baseId);
    const base_ids = [...current];
    const default_base_id = base_ids.includes(u.default_base_id) ? u.default_base_id : (base_ids[0] || null);
    try {
      await api.patch(`/bases/users/${u.id}`, { base_ids, default_base_id });
      load();
    } catch (_e) {
      toast.error("Could not update user's bases");
    }
  };

  const setDefaultBase = async (u, baseId) => {
    const base_ids = (u.base_ids || []).includes(baseId) ? u.base_ids : [...(u.base_ids || []), baseId];
    try {
      await api.patch(`/bases/users/${u.id}`, { base_ids, default_base_id: baseId });
      toast.success("Default base set (used for bot entries)");
      load();
    } catch (_e) {
      toast.error("Could not set default base");
    }
  };

  const baseName = (id) => bases.find((b) => b.id === id)?.name || "—";

  return (
    <div data-testid="locations-page">
      <PageHeader
        eyebrow="Operations"
        title="Bases & Locations"
        subtitle="Create geographic bases (e.g. South Africa, Accra) and control who can see each. Everyone keeps the same categories — the overview expands to cover every base."
      />

      {loading ? (
        <div className="py-20 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* ── Bases ── */}
          <div className="space-y-6">
            <Card className="rounded-xl" data-testid="bases-create-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Plus className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> Add a base
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Base name</Label>
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Accra" data-testid="base-name-input" />
                </div>
                <div className="w-full sm:w-24">
                  <Label className="text-xs">Code</Label>
                  <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="GH" maxLength={8} data-testid="base-code-input" />
                </div>
                <Button onClick={createBase} disabled={creating} data-testid="base-create-btn"
                  className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90 shrink-0">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </CardContent>
            </Card>

            <Card className="rounded-xl" data-testid="bases-list-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> Bases
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="bases-list">
                {bases.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border p-3" data-testid={`base-${b.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {b.name} {b.code ? <span className="text-xs text-muted-foreground">({b.code})</span> : null}
                        </div>
                        {b.is_primary && <Badge variant="secondary" className="mt-0.5">First base</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">{b.active ? "Active" : "Hidden"}</span>
                      <Switch checked={!!b.active} disabled={b.is_primary} onCheckedChange={() => toggleActive(b)} data-testid={`base-active-${b.id}`} />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* ── User assignments ── */}
          <Card className="rounded-xl" data-testid="base-users-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> Who can see each base
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="base-users-list">
              <p className="text-xs text-muted-foreground">
                Tick the bases a person may access. Their <strong>default base</strong> (★) is where their WhatsApp/Telegram entries are filed. Master admins always see every base.
              </p>
              {users.map((u) => (
                <div key={u.id} className="rounded-lg border p-3" data-testid={`base-user-${u.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{u.name || u.username || u.email}</div>
                      <div className="text-xs text-muted-foreground">{u.role || "—"}{u.is_master ? " · master (all bases)" : ""}</div>
                    </div>
                  </div>
                  {!u.is_master && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {bases.map((b) => {
                        const on = (u.base_ids || []).includes(b.id);
                        const isDefault = u.default_base_id === b.id;
                        return (
                          <div key={b.id} className="inline-flex items-center">
                            <button
                              onClick={() => toggleUserBase(u, b.id)}
                              data-testid={`assign-${u.id}-${b.id}`}
                              className={`text-xs rounded-l-full border px-2.5 py-1 transition-colors ${on ? "bg-[hsl(var(--ap-forest))] text-white border-[hsl(var(--ap-forest))]" : "bg-background hover:bg-accent"}`}
                            >
                              {b.name}
                            </button>
                            <button
                              onClick={() => setDefaultBase(u, b.id)}
                              title="Set as default base (for bot entries)"
                              data-testid={`default-${u.id}-${b.id}`}
                              className={`text-xs rounded-r-full border border-l-0 px-1.5 py-1 ${isDefault ? "bg-[hsl(var(--ap-brass))] text-[hsl(var(--ap-saddle))] border-[hsl(var(--ap-brass))]" : "bg-background hover:bg-accent text-muted-foreground"}`}
                            >
                              ★
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
